import { computed, ref, watch } from "vue";
import { defineStore, storeToRefs } from "pinia";

import { useSurveyStore } from "../survey";
import { useContractsStore } from "@stores/contracts";
import { useCurrentUserStore } from "@stores/currentUser";
import {
  getCreditRules,
  QUANTITATIVE_RESPONDENTS_PER_CREDIT,
  QUALITATIVE_RESPONDENTS_PER_CREDIT,
} from "@utils/credits/creditRules";

export const NO_CONTRACT_VALUE = "no_contract";

// Re-exported for existing consumers (e.g. ImportSurveyDataDialog).
export { QUANTITATIVE_RESPONDENTS_PER_CREDIT, QUALITATIVE_RESPONDENTS_PER_CREDIT };

export const useSurveyCreditsStore = defineStore("surveyCredits", () => {
  const surveyStore = useSurveyStore();
  const contractsStore = useContractsStore();
  const currentUserStore = useCurrentUserStore();
  const { survey } = storeToRefs(surveyStore);
  const { setupContracts } = storeToRefs(contractsStore);
  const { isAdmin, isSale } = storeToRefs(currentUserStore);

  const contractChoicePending = ref(false);
  const requireContractChoice = () => (contractChoicePending.value = true);

  const currentSurveyId = computed(() => survey.value?.id ?? null);
  const currentContractId = computed(() => survey.value?.contract_id ?? null);
  const currentCreditsMax = computed(() => survey.value?.credits_max ?? 1);
  const currentCreditsAuto = computed(() => survey.value?.credits_consumed_auto ?? 0);
  const currentCreditsManual = computed(() => survey.value?.credits_consumed_manual);
  const hasManualOverride = computed(
    () => currentCreditsManual.value !== null && currentCreditsManual.value !== undefined,
  );
  const creditEffective = computed(
    () => survey.value?.credits_consumed_manual ?? survey.value?.credits_consumed_auto_effective ?? 0,
  );
  // Two sources of truth depending on lifecycle:
  // - During creation, only design.type is set by the user (the ai_chat column
  //   is derived server-side at save time).
  // - On edit, the column ai_chat reflects the persisted state.
  // OR them so the credits stepper reacts as soon as the user picks
  // "Qualitative (AI chat)" in SurveyNew.
  const isAiChat = computed(
    () => Boolean(survey.value?.ai_interview) || survey.value?.design?.type === "ai_chat",
  );

  const selectedContract = computed(() =>
    setupContracts.value.find((c) => c.id === currentContractId.value),
  );
  const isFreemiumContract = computed(() =>
    (selectedContract.value?.accounts ?? []).some((account) => account.freemium),
  );

  const creditRules = computed(() =>
    getCreditRules({ aiChat: isAiChat.value, freemiumAccount: isFreemiumContract.value }),
  );
  const stepperIncrement = computed(() => creditRules.value.stepperIncrement);
  const respondentsPerCredit = computed(() => creditRules.value.respondentsPerCredit);
  const ruleLabel = computed(() => creditRules.value.ruleLabel);
  const accessibleRespondents = computed(() => currentCreditsMax.value * respondentsPerCredit.value);

  const consumedAutoEffective = computed(() => survey.value?.credits_consumed_auto_effective ?? 0);

  const minCreditsMax = computed(() => Math.max(stepperIncrement.value, consumedAutoEffective.value));
  const isAtConsumedFloor = computed(() => currentCreditsMax.value <= consumedAutoEffective.value);

  const isContractExhausted = computed(
    () => Boolean(selectedContract.value) && (selectedContract.value.credits_remaining ?? 0) <= 0,
  );

  // True when a client (no access to the "No contract" option) submits the
  // form without having picked a contract. Used to gate the OK button at
  // creation. Admin/Sales can validly have contract_id = null via the
  // explicit "No contract" choice — they're never considered missing.
  const canSelectNoContract = computed(() => isAdmin.value || isSale.value);
  const isContractMissing = computed(
    () => currentContractId.value === null && !canSelectNoContract.value,
  );

  const updateContractId = (value) => {
    const newId = value === NO_CONTRACT_VALUE ? null : parseInt(value, 10);
    survey.value.contract_id = newId;
    contractChoicePending.value = false;

    // Clamp credits_max to the new contract's remaining credits to avoid over-allocation
    // when switching from a contract with more credits to one with fewer.
    if (newId !== null) {
      const newContract = setupContracts.value.find((c) => c.id === newId);
      const remaining = newContract?.credits_remaining ?? Infinity;
      if (currentCreditsMax.value > remaining) {
        survey.value.credits_max = Math.max(stepperIncrement.value, remaining);
      }
    }
  };

  // Snap to the next multiple of step (1 → 5 → 10), not credits_max + step.
  const nextCreditsMax = computed(() => {
    const step = stepperIncrement.value;
    return Math.floor(currentCreditsMax.value / step) * step + step;
  });

  const incrementCreditsMax = () => {
    survey.value.credits_max = nextCreditsMax.value;
  };

  const decrementCreditsMax = () => {
    const step = stepperIncrement.value;
    const next = Math.ceil(currentCreditsMax.value / step) * step - step;
    if (next >= minCreditsMax.value) survey.value.credits_max = next;
  };

  // Keep credits_max aligned to the stepper increment for ai_chat surveys
  // during creation. The DB default is 1 (for the quanti default template) so
  // a brand-new ai_chat survey would otherwise start at 1 and stepper "+" by
  // 5 would land on 6, 11, 16… Skip on edit (token set) so we never silently
  // mutate persisted data.
  watch(
    isAiChat,
    (aiChat) => {
      if (!survey.value) return;
      if (survey.value.token) return;
      if (!aiChat) return;
      const step = stepperIncrement.value;
      const snapped = Math.max(step, Math.ceil((currentCreditsMax.value || 0) / step) * step);
      if (snapped !== currentCreditsMax.value) survey.value.credits_max = snapped;
    },
    { immediate: true },
  );

  return {
    // getters
    currentSurveyId,
    currentContractId,
    currentCreditsMax,
    currentCreditsAuto,
    currentCreditsManual,
    hasManualOverride,
    creditEffective,
    isAiChat,
    stepperIncrement,
    respondentsPerCredit,
    ruleLabel,
    accessibleRespondents,
    consumedAutoEffective,
    minCreditsMax,
    isAtConsumedFloor,
    nextCreditsMax,
    selectedContract,
    isContractExhausted,
    isContractMissing,
    contractChoicePending,
    // actions
    updateContractId,
    incrementCreditsMax,
    decrementCreditsMax,
    requireContractChoice,
  };
});
